import { FF_ESCRIPT_RECONCILE_PRESCRIPTIONS } from '@/constants/featureFlags';
import type { reconcilePrescriptions } from '@montugroup/circuit-api-contracts';
import { getTsCustomerPortalClient } from '@montugroup/circuit-shared/api/customer-portal.client';
import type { TsRestResponseError } from '@ts-rest/core';
import { useMemo } from 'react';
import useFeatureFlags from '../useFeatureFlags';

export type UseMutationReconcilePrescriptionsError = TsRestResponseError<typeof reconcilePrescriptions>;

const client = getTsCustomerPortalClient();

export const useReconcilePrescriptions = (prescriptionIds: number[]) => {
  const {
    isPending,
    isError,
    isSuccess,
    data: reconcilePrescriptionsData,
    error,
    mutate,
    mutateAsync,
  } = client.reconcilePrescriptions.useMutation({
    meta: {
      error: {
        message: 'Failed to reconcile prescriptions',
        showToast: true,
      },
    },
  });

  const mutateWithPrescriptionIds = () => {
    if (prescriptionIds.length > 0) {
      return mutate({ body: { data: { prescriptionIds } } });
    }
    return;
  };

  const mutateAsyncWithPrescriptionIds = async () => {
    if (prescriptionIds.length > 0) {
      return mutateAsync({ body: { data: { prescriptionIds } } });
    }
    return undefined;
  };

  return {
    mutate: mutateWithPrescriptionIds,
    mutateAsync: mutateAsyncWithPrescriptionIds,
    query: {
      isLoading: isPending,
      isError,
      error: error as UseMutationReconcilePrescriptionsError | null,
      isSuccess,
      refetch: mutateWithPrescriptionIds,
    },
    data: reconcilePrescriptionsData?.body.data,
  };
};

export const useReconcilePrescriptionsFFWrapper = (prescriptionIds: (number | undefined)[]) => {
  const { flags } = useFeatureFlags();

  const filteredPrescriptionIds = prescriptionIds.filter((id): id is number => id !== undefined);

  const {
    query: { isLoading, isError, isSuccess },
    data: reconcilePrescriptionsData,
    mutate,
    mutateAsync,
  } = useReconcilePrescriptions(filteredPrescriptionIds);

  const query = useMemo(
    () => ({
      isLoading: flags[FF_ESCRIPT_RECONCILE_PRESCRIPTIONS] ? isLoading : false,
      isError: flags[FF_ESCRIPT_RECONCILE_PRESCRIPTIONS] ? isError : false,
      isSuccess: flags[FF_ESCRIPT_RECONCILE_PRESCRIPTIONS] ? isSuccess : true,
    }),
    [isLoading, isError, isSuccess, flags],
  );

  return {
    mutate: flags[FF_ESCRIPT_RECONCILE_PRESCRIPTIONS] ? mutate : () => {},
    mutateAsync: flags[FF_ESCRIPT_RECONCILE_PRESCRIPTIONS] ? mutateAsync : async () => undefined,
    query,
    reconcilePrescriptionsData: flags[FF_ESCRIPT_RECONCILE_PRESCRIPTIONS]
      ? reconcilePrescriptionsData
      : { valid: true },
  };
};
